import { useState, useCallback, useMemo } from 'react';
import { getReservations } from '../services/apiClient';
import { getTodayLocal, getDateOffsetLocal } from '../utils/formatters';
import logger from '../utils/logger';

// Number of days back that the default ('30d') date window covers.
export const DEFAULT_WINDOW_DAYS = 30;

export const useReservations = (token, apiUrl, onLogout) => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    futureOnly: 'all',
    dateWindow: '30d', // '30d' | 'all' — limits how far back reservations are shown
    status: '',
    date: '',
    search: '',
  });

  // `windowDays` is how far back to ask the server for, or null for everything.
  //
  // It is an explicit argument rather than being read from `filters` so that
  // this callback's identity only depends on token/apiUrl/onLogout. If it
  // changed whenever a filter changed, every effect that lists it as a
  // dependency would refire — and the pages here define `onLogout` inline, so
  // its identity is already new on every render.
  //
  // The client-side filtering below is unchanged and still authoritative for
  // what the user sees. This only narrows what travels over the network.
  const fetchReservations = useCallback(
    async (showLoading = true, windowDays = DEFAULT_WINDOW_DAYS) => {
      if (!token) {
        logger.warn('fetchReservations called without token');
        return;
      }
      if (showLoading) setLoading(true);
      setError(null);

      const from = windowDays === null ? undefined : getDateOffsetLocal(windowDays);
      logger.info('Fetching reservations', { showLoading, windowDays, from });

      try {
        const data = await getReservations(apiUrl, token, { from });
        if (data.status === 'success' && data.reservations) {
          logger.info('Reservations fetched successfully', { count: data.reservations.length });
          setReservations(data.reservations);
        } else {
          logger.error('Unexpected response format from reservations API', { data });
          setError('Formato de resposta inesperado do servidor');
        }
      } catch (err) {
        logger.error('Failed to fetch reservations', { error: err.message, status: err.status });
        if (err.status === 401 && onLogout) {
          logger.warn('Unauthorized - logging out');
          onLogout();
        } else {
          setError(err.message || 'Erro de rede ao buscar reservas.');
        }
      } finally {
        if (showLoading) setLoading(false);
      }
    },
    [token, apiUrl, onLogout]
  );

  const handleReservationUpdate = (updatedReservation) => {
    setReservations((prevReservations) =>
      prevReservations.map((res) =>
        res.id === updatedReservation.id ? updatedReservation : res
      )
    );
  };

  const handleReservationCreate = (newReservation) => {
    setReservations((prevReservations) => [newReservation, ...prevReservations]);
  };

  const applyFilters = useCallback(
    (reservationsToFilter) => {
      const currentDate = getTodayLocal();
      const windowStart = getDateOffsetLocal(DEFAULT_WINDOW_DAYS);
      const searchTerm = filters.search.trim().toLowerCase();

      return reservationsToFilter.filter((reservation) => {
        // Future only filter — cancelled reservations never count as "future"
        if (filters.futureOnly === 'future') {
          if (reservation.booking_date < currentDate) return false;
          if (reservation.state === 'canceled') return false;
        }

        // Default date window: only show the last 30 days onward unless "all"
        if (filters.dateWindow === '30d' && reservation.booking_date < windowStart) {
          return false;
        }

        // Status filter
        if (filters.status && reservation.state !== filters.status) {
          return false;
        }

        // Date filter (exact day)
        if (filters.date && reservation.booking_date !== filters.date) {
          return false;
        }

        // Free-text search over customer name and phone
        if (searchTerm) {
          const name = (reservation.name || '').toLowerCase();
          const phone = (reservation.phone || '').toLowerCase();
          if (!name.includes(searchTerm) && !phone.includes(searchTerm)) {
            return false;
          }
        }

        return true;
      });
    },
    [filters]
  );

  const filteredReservations = useMemo(() => {
    const filtered = applyFilters(reservations);
    // Sort by date DESC, then by from_time ASC
    return filtered.sort((a, b) => {
      const dateCompare = b.booking_date.localeCompare(a.booking_date);
      if (dateCompare !== 0) return dateCompare;
      return a.from_time.localeCompare(b.from_time);
    });
  }, [reservations, applyFilters]);

  return {
    reservations,
    loading,
    error,
    filters,
    setFilters,
    fetchReservations,
    handleReservationUpdate,
    handleReservationCreate,
    filteredReservations,
  };
};
